// The Combinations property panel (prototype `propsCombos`): how sets
// combine, which view suits what, and the sets in screen order.

import { AlertTriangle, ArrowDown, ArrowUp, Grid2x2, Info } from 'lucide-react'
import { formOf } from './model'
import { opForm, useSetup } from './state'
import { setCover } from './coverage'
import { useValuesByDriver } from './StepCombinations'
import { HintBox, SetDot } from './ui'

export default function CombinationsPanel() {
  const { formNo, draft, mode } = useSetup()
  const form = formOf(draft.layer, formNo)
  const { values } = useValuesByDriver(formNo)
  const sets = form?.sets || []
  // A standard set keeps its place (the application orders them); an
  // organization orders its own sets among themselves.
  const movable = (i: number) => mode === 'design' || sets[i]?.origin !== 'design'
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 0 || j >= sets.length || !movable(i) || !movable(j)) return
    const a = sets[i].key
    const b = sets[j].key
    draft.edit(opForm(formNo, f => {
      const ia = f.sets.findIndex(s => s.key === a)
      const ib = f.sets.findIndex(s => s.key === b)
      if (ia < 0 || ib < 0) return
      const t = f.sets[ia]; f.sets[ia] = f.sets[ib]; f.sets[ib] = t
    }))
  }
  return (
    <>
      <div className="uds-props-h">
        <div className="row"><span className="uds-pill uds-p-acc">Combinations</span></div>
        <h2>How attribute sets combine</h2>
      </div>
      <div className="uds-pbody" style={{ display: 'grid', gap: 10 }}>
        <HintBox icon={<Info size={15} />} style={{ margin: 0 }}>
          A record gets the columns of <b>every</b> set that matches it. Inside one set, values in a cell are &quot;or&quot;,
          and the cells of a row are &quot;and&quot;.
        </HintBox>
        <HintBox icon={<Grid2x2 size={15} />} style={{ margin: 0 }}>
          <b>Rules</b> is quickest when a set covers whole groups of values. <b>Grid</b> is quickest for exceptions:
          paint single cells, rows or columns.
        </HintBox>
        <HintBox icon={<AlertTriangle size={15} />} style={{ margin: 0 }}>
          <b>Any</b> also covers values added to the master later. A list of picked values does not, and Refresh flags
          new values.
        </HintBox>
        <label className="uds-lbl">Sets, in screen order</label>
        {sets.map((s, i) => (
          <div key={s.key} className="uds-li" style={{ marginTop: 0 }}>
            <SetDot color={s.color} /><span className="t">{s.label || s.key}</span>
            <span style={{ color: 'var(--color-text-faint)' }}>{setCover(form!, s.key, values).covered.toLocaleString()}</span>
            <button type="button" className="uds-icbtn" aria-label="Move up" disabled={!movable(i) || !movable(i - 1) || i === 0} onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
            <button type="button" className="uds-icbtn" aria-label="Move down" disabled={!movable(i) || !movable(i + 1) || i === sets.length - 1} onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
          </div>
        ))}
        {!sets.length && <span className="uds-na">No attribute sets yet.</span>}
      </div>
    </>
  )
}
